import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { link } from 'chaoskit/src/assets/styles/utility'
import { StylesAvatarVariables } from 'chaoskit/src/components/Avatar'

import TextLink from '~components/TextLink'
import Image from '~components/Image'
import useAuthor from '~hooks/useAuthor'

const AuthorWrapper = ({ url, size, ...rest }) => {
  const theme = useTheme()

  const Component = url ? TextLink : 'div'

  return (
    <Component
      css={[
        url && link.reset(theme),

        {
          display: 'grid',
        },

        size === 'base' && {
          gridTemplateColumns: 'auto 1fr',
          alignItems: 'center',
          gap: theme.space.base,
        },

        size === 'large' && {
          gap: theme.space.small,
        },
      ]}
      href={url}
      {...rest}
    />
  )
}

AuthorWrapper.propTypes = {
  url: PropTypes.string,
  size: PropTypes.oneOf(['base', 'large']),
}

const AuthorContent = ({ components, author, size }) => {
  const theme = useTheme()

  return (
    <Fragment>
      {components.includes('avatar') && (
        <Image
          css={{
            width:
              size === 'large' ? 120 : StylesAvatarVariables(theme).size.base,
          }}
          src={author.frontmatter.photo}
          alt={author.frontmatter.title}
        />
      )}
      {(components.includes('name') || components.includes('position')) && (
        <div
          css={{
            display: 'grid',
            gap: size === 'large' ? theme.space.xsmall : 0,
            lineHeight: theme.lineHeight.small,
          }}
        >
          {components.includes('name') && (
            <div
              css={[
                size === 'base' && {
                  fontSize: theme.fontSize.medium,
                },

                size === 'large' && {
                  fontSize: theme.fontSize.large,
                },

                {
                  fontWeight: theme.fontWeight.bold,
                },
              ]}
            >
              {author.frontmatter.title}
            </div>
          )}
          {components.includes('position') && (
            <div
              css={[
                {
                  color: theme.fontColor.muted,
                },

                size === 'base' && {
                  fontSize: theme.fontSize.small,
                },
              ]}
            >
              {author.frontmatter.position}
            </div>
          )}
        </div>
      )}
    </Fragment>
  )
}

AuthorContent.propTypes = {
  author: PropTypes.object.isRequired,
  components: PropTypes.array,
  size: PropTypes.oneOf(['base', 'large']),
}

const Author = ({
  components = ['avatar', 'name', 'position'],
  name,
  url,
  size = 'base',
  ...rest
}) => {
  const author = useAuthor(name)

  return (
    <AuthorWrapper url={url} size={size} {...rest}>
      <AuthorContent components={components} author={author} size={size} />
    </AuthorWrapper>
  )
}

Author.propTypes = {
  components: PropTypes.array,
  name: PropTypes.string,
  url: PropTypes.string,
  size: PropTypes.oneOf(['base', 'large']),
}

export default Author
